import React, { useState } from "react";
import { Smartphone, ArrowLeft } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";

// E.164 quick check — '+' + 8-15 digits. Server enforces the canonical
// regex too; this is just for instant feedback in the form.
const E164_RE = /^\+[1-9]\d{7,14}$/;

// SMSSignInForm — two-step phone sign-in.
//   1) Enter phone in E.164 (+15551234567)  → POST /auth/sms/send
//   2) Enter the 6-digit code               → POST /auth/sms/verify → JWT
const SMSSignInForm = ({ onSuccess, onBack }) => {
  const { requestSMSCode, verifySMSCode } = useAuth();
  const [phone, setPhone] = useState(() => {
    try { return localStorage.getItem("ge:lastPhone") || "+1"; } catch (_) { return "+1"; }
  });
  const [code, setCode] = useState("");
  const [step, setStep] = useState("phone"); // 'phone' | 'code'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSend = async (e) => {
    e.preventDefault();
    setError("");
    const trimmed = (phone || "").trim().replace(/\s+/g, "");
    if (!E164_RE.test(trimmed)) {
      setError("Enter your phone in E.164 format (e.g. +14155552671).");
      return;
    }
    setLoading(true);
    try {
      await requestSMSCode(trimmed);
      try { localStorage.setItem("ge:lastPhone", trimmed); } catch (_) { /* noop */ }
      setPhone(trimmed);
      setStep("code");
      toast.success("Code sent. Check your messages.");
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't send the code. Try again.");
    }
    setLoading(false);
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError("");
    const clean = (code || "").replace(/\D/g, "");
    if (!/^\d{4,8}$/.test(clean)) {
      setError("Enter the 6-digit code from your SMS.");
      return;
    }
    setLoading(true);
    try {
      await verifySMSCode(phone, clean);
      toast.success("Welcome to GlobalEyes!");
      onSuccess && onSuccess();
    } catch (err) {
      setError(err?.response?.data?.detail || "That code is invalid or expired.");
    }
    setLoading(false);
  };

  if (step === "code") {
    return (
      <form onSubmit={handleVerify} className="space-y-4" data-testid="auth-form-sms-code">
        <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-3">
          <p className="text-xs text-gray-300">
            We sent a 6-digit code to{" "}
            <span className="text-emerald-400 font-medium">{phone}</span>
          </p>
        </div>
        <div>
          <Label htmlFor="sms-code" className="text-gray-300">Verification code</Label>
          <Input
            id="sms-code"
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            className="bg-gray-800 border-gray-700 text-white mt-1 text-center tracking-[0.5em] text-lg"
            placeholder="123456"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={8}
            required
            data-testid="sms-code-input"
          />
        </div>
        {error && <p className="text-red-400 text-sm" data-testid="auth-error">{error}</p>}
        <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={loading} data-testid="sms-verify-submit">
          {loading ? "Verifying..." : "Verify & Sign in"}
        </Button>
        <button
          type="button"
          onClick={() => { setStep("phone"); setCode(""); setError(""); }}
          className="block w-full text-center text-xs text-gray-500 hover:text-gray-300 transition-colors pt-1"
          data-testid="sms-change-phone-btn"
        >
          <ArrowLeft className="inline h-3 w-3 mr-1" /> Use a different phone number
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={handleSend} className="space-y-4" data-testid="auth-form-sms">
      <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-3 flex items-start gap-2.5">
        <Smartphone className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" />
        <div className="text-xs text-gray-300 leading-relaxed">
          <span className="font-semibold text-emerald-400">Sign in by SMS.</span>{" "}
          We'll text you a one-time code.
        </div>
      </div>
      <div>
        <Label htmlFor="sms-phone" className="text-gray-300">Phone (with country code)</Label>
        <Input
          id="sms-phone"
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          className="bg-gray-800 border-gray-700 text-white mt-1"
          placeholder="+14155552671"
          required
          autoCapitalize="none" autoCorrect="off" spellCheck={false} inputMode="tel"
          data-testid="sms-phone-input"
        />
        <p className="text-[11px] text-gray-500 mt-1">
          Use E.164 format (e.g. +1 for US/Canada, +44 UK, +61 AU).
        </p>
      </div>
      {error && <p className="text-red-400 text-sm" data-testid="auth-error">{error}</p>}
      <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={loading} data-testid="sms-send-submit">
        <Smartphone className="h-4 w-4 mr-2" />
        {loading ? "Sending..." : "Text me a sign-in code"}
      </Button>
      <button
        type="button"
        onClick={onBack}
        className="block w-full text-center text-xs text-gray-500 hover:text-gray-300 transition-colors pt-1"
        data-testid="sms-back-btn"
      >
        <ArrowLeft className="inline h-3 w-3 mr-1" /> Back to email sign-in
      </button>
    </form>
  );
};

export default SMSSignInForm;
